import type { ReactNode } from 'react';

import { getCurrentUser, signInWithRedirect } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { useCallback, useEffect, useState } from 'react';

type AuthStatus = 'authenticated' | 'checking' | 'unauthenticated';

const ADOPT_POLL_INTERVAL_MS = 2000;
// Marks a tab opened by the embedded guest's "Sign In" fallback, distinct from a normal top-level
// visit — written to sessionStorage (not just the query string) so it survives the full-page
// Cognito redirect round trip, which navigates this same tab away from and back to a different path.
const AUTH_POPUP_SESSION_KEY = 'ce-auth-popup';
const AUTH_POPUP_QUERY_PARAM = 'ce-auth-popup';

const IDENTITY_PROVIDER = import.meta.env.VITE_COGNITO_IDENTITY_PROVIDER;
const COGNITO_KEY_PREFIX = `CognitoIdentityServiceProvider.${import.meta.env.VITE_COGNITO_CLIENT_ID}.`;
// Amplify also keeps transient, in-progress-OAuth-attempt bookkeeping under this same prefix
// (`oauthPKCE`/`oauthState`/`inflightOAuth`/`inflightOAuthDeadline`) — copying those alone (with
// no actual tokens) is not a signed-in session, so only these suffixes count as "adopted".
const COGNITO_SESSION_KEY_SUFFIXES = [
  'LastAuthUser',
  'accessToken',
  'idToken',
  'refreshToken',
  'clockDrift',
  'oauthMetadata',
  'oauthSignIn',
];

// The Cognito/SAML redirect needs a top-level tab, not the sandboxed embedding iframe.
const isEmbedded = (() => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();

const isAuthPopup = (() => {
  if (new URLSearchParams(window.location.search).has(AUTH_POPUP_QUERY_PARAM)) {
    try {
      window.sessionStorage.setItem(AUTH_POPUP_SESSION_KEY, '1');
    } catch {
      // sessionStorage may be unavailable (e.g. private browsing) — the query param still marks this load.
    }
    return true;
  }
  try {
    return window.sessionStorage.getItem(AUTH_POPUP_SESSION_KEY) === '1';
  } catch {
    return false;
  }
})();

const isCognitoSessionKey = (key: string): boolean =>
  key.startsWith(COGNITO_KEY_PREFIX) && COGNITO_SESSION_KEY_SUFFIXES.some(suffix => key.endsWith(`.${suffix}`));

/**
 * Copies Cognito's token keys out of the unpartitioned localStorage the Storage Access API
 * exposes into this iframe's own (storage-partitioned) localStorage, where Amplify actually
 * reads from. Requires `allow-storage-access-by-user-activation` on the embedding iframe, and
 * the first grant per `<top-level site, embedded site>` pair requires a user gesture (a click).
 * Returns whether a real, usable session (an access token) was found and copied.
 */
const adoptUnpartitionedSession = async (): Promise<boolean> => {
  if (!('requestStorageAccess' in document)) return false;

  try {
    const handle = await document.requestStorageAccess({ localStorage: true });
    const unpartitioned = handle.localStorage;
    if (!unpartitioned) return false;

    let copiedAccessToken = false;
    for (let i = 0; i < unpartitioned.length; i += 1) {
      const key = unpartitioned.key(i);
      const value = key && isCognitoSessionKey(key) ? unpartitioned.getItem(key) : null;
      if (key && value !== null) {
        window.localStorage.setItem(key, value);
        if (key.endsWith('.accessToken')) copiedAccessToken = true;
      }
    }
    return copiedAccessToken;
  } catch {
    return false;
  }
};

/**
 * Gates the app behind Cognito/Salesforce SAML sign-in. Unauthenticated users
 * are redirected immediately; nothing renders until sign-in is confirmed.
 */
export const AuthGate = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<AuthStatus>('checking');
  const [signInPopup, setSignInPopup] = useState<null | Window>(null);

  const checkCurrentUser = useCallback(() => {
    getCurrentUser()
      .then(() => {
        setStatus('authenticated');
      })
      .catch(() => {
        setStatus('unauthenticated');
      });
  }, []);

  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        checkCurrentUser();
      }
      if (payload.event === 'signInWithRedirect_failure') {
        setStatus('unauthenticated');
      }
    });

    checkCurrentUser();
    return unsubscribe;
  }, [checkCurrentUser]);

  useEffect(() => {
    if (status === 'unauthenticated' && !isEmbedded) {
      void signInWithRedirect({ provider: { custom: IDENTITY_PROVIDER } });
    }
    // Amplify leaves the OAuth callback/logout path in the URL after completing the token exchange.
    if (status === 'authenticated' && !isAuthPopup && window.location.pathname !== '/') {
      window.history.replaceState({}, '', '/');
    }
  }, [status]);

  // This tab only exists to sign the user in for the embedded guest — close it once that's done.
  // The opener picks up the adopted session on its own (see the polling effect below).
  useEffect(() => {
    if (status === 'authenticated' && isAuthPopup) {
      window.close();
    }
  }, [status]);

  // A permission already granted in a prior session doesn't need a fresh user gesture — retry silently.
  useEffect(() => {
    if (status !== 'unauthenticated' || !isEmbedded) return;
    void adoptUnpartitionedSession().then(copied => {
      if (copied) checkCurrentUser();
    });
  }, [status, checkCurrentUser]);

  // While the sign-in popup is open, poll for the session it adopts instead of making the user
  // come back and click Sign In again. Always attempt the adopt first — the popup closes itself
  // the instant it's authenticated, which can easily race ahead of a fixed poll interval, so
  // bailing out as soon as `closed` is seen (without one last adopt attempt) misses the tokens.
  useEffect(() => {
    if (!signInPopup || status !== 'unauthenticated' || !isEmbedded) return;

    let interval: ReturnType<typeof setInterval>;
    const poll = () => {
      const popupWasClosed = signInPopup.closed;
      void adoptUnpartitionedSession().then(copied => {
        if (copied) {
          checkCurrentUser();
          if (!signInPopup.closed) signInPopup.close();
          clearInterval(interval);
          setSignInPopup(null);
        } else if (popupWasClosed) {
          clearInterval(interval);
          setSignInPopup(null);
        }
      });
    };

    poll();
    interval = setInterval(poll, ADOPT_POLL_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
  }, [signInPopup, status, checkCurrentUser]);

  const handleSignInClick = () => {
    void adoptUnpartitionedSession().then(copied => {
      if (copied) {
        checkCurrentUser();
      } else {
        setSignInPopup(window.open(`${import.meta.env.VITE_APP_URL}?${AUTH_POPUP_QUERY_PARAM}=1`, '_blank'));
      }
    });
  };

  if (isAuthPopup) {
    return (
      <div className="slds-p-around_large slds-text-align_center">
        <p className="slds-text-heading_small slds-m-bottom_small">
          {status === 'authenticated' ? 'Signed in' : 'Signing in\u2026'}
        </p>
        <p>
          {status === 'authenticated'
            ? 'You can close this window.'
            : 'Complete sign-in above, then this window will close automatically.'}
        </p>
      </div>
    );
  }

  if (status === 'unauthenticated' && isEmbedded) {
    return (
      <div className="slds-p-around_large slds-text-align_center">
        <p className="slds-text-heading_small slds-m-bottom_small">Verify access to Complex Contracts</p>
        <p className="slds-m-bottom_small">
          Continue in a new tab to connect your Salesforce account. The tab closes when verification finishes, and this
          page continues automatically.
        </p>
        <button className="slds-button slds-button_brand" onClick={handleSignInClick} type="button">
          Verify access
        </button>
      </div>
    );
  }

  if (status !== 'authenticated') {
    return null;
  }

  return children;
};
